import { useCallback, useEffect, useRef, useState } from 'react'
import { AuthChangeEvent, Session, User as SupabaseUser } from '@supabase/supabase-js'
import { supabase, setRememberMe, updateLastActivity, shouldExpireSession } from '@/lib/supabaseClient'
import type { AuthState, LoginCredentials, SignUpCredentials, User } from '@/types'
import { AuthContext, type AuthContextValue } from '@/contexts/auth-context'

function convertSupabaseUser(supabaseUser: SupabaseUser | null): User | null {
  if (!supabaseUser) return null
  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    user_metadata: supabaseUser.user_metadata as User['user_metadata'],
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  })
  const userMetadataRef = useRef(authState.user?.user_metadata)
  userMetadataRef.current = authState.user?.user_metadata

  useEffect(() => {
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        if (error) {
          setAuthState({ user: null, loading: false, error: error.message })
          return
        }
        if (session && shouldExpireSession()) {
          await supabase.auth.signOut()
          setAuthState({ user: null, loading: false, error: null })
          return
        }
        if (session) {
          updateLastActivity()
        }
        setAuthState({
          user: convertSupabaseUser(session?.user ?? null),
          loading: false,
          error: null,
        })
      } catch {
        setAuthState({ user: null, loading: false, error: 'Failed to get session' })
      }
    }
    getInitialSession()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event: AuthChangeEvent, session: Session | null) => {
        if (session) {
          updateLastActivity()
        }
        setAuthState({
          user: convertSupabaseUser(session?.user ?? null),
          loading: false,
          error: null,
        })
      }
    )
    return () => subscription.unsubscribe()
  }, [])

  const signUp = useCallback(async (credentials: SignUpCredentials) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))
    try {
      const { data, error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          emailRedirectTo: `${window.location.origin}/portal`,
          data: {
            full_name: credentials.full_name ?? '',
            first_name: credentials.first_name ?? '',
            last_name: credentials.last_name ?? '',
            marketing_consent: credentials.marketing_consent ?? false,
          },
        },
      })
      if (error) {
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error: error.message }
      }
      const userFromResponse = data?.user ? convertSupabaseUser(data.user) : null
      setAuthState(prev => ({
        ...prev,
        user: userFromResponse ?? prev.user,
        loading: false,
        error: null,
      }))
      return { data }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign up failed'
      setAuthState(prev => ({ ...prev, loading: false, error: msg }))
      return { error: msg }
    }
  }, [])

  const signIn = useCallback(async (credentials: LoginCredentials) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))
    setRememberMe(credentials.remember_me ?? true)
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      })
      if (error) {
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error: error.message }
      }
      const userFromResponse = data?.user ? convertSupabaseUser(data.user) : null
      setAuthState(prev => ({
        ...prev,
        user: userFromResponse ?? prev.user,
        loading: false,
        error: null,
      }))
      return { data }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign in failed'
      setAuthState(prev => ({ ...prev, loading: false, error: msg }))
      return { error: msg }
    }
  }, [])

  const signOut = useCallback(async () => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error: error.message }
      }
      setAuthState({ user: null, loading: false, error: null })
      return { success: true }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign out failed'
      setAuthState(prev => ({ ...prev, loading: false, error: msg }))
      return { error: msg }
    }
  }, [])

  const resetPassword = useCallback(async (email: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })
      if (error) {
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error: error.message }
      }
      setAuthState(prev => ({ ...prev, loading: false }))
      return { success: true }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Password reset failed'
      setAuthState(prev => ({ ...prev, loading: false, error: msg }))
      return { error: msg }
    }
  }, [])

  const updatePassword = useCallback(async (password: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) {
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error: error.message }
      }
      setAuthState(prev => ({ ...prev, loading: false }))
      return { success: true }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Password update failed'
      setAuthState(prev => ({ ...prev, loading: false, error: msg }))
      return { error: msg }
    }
  }, [])

  const updateUserMetadata = useCallback(async (metadata: Record<string, unknown>) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }))
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: { ...userMetadataRef.current, ...metadata },
      })
      if (error) {
        setAuthState(prev => ({ ...prev, loading: false }))
        return { error: error.message }
      }
      setAuthState(prev => ({
        ...prev,
        user: data?.user ? convertSupabaseUser(data.user) : prev.user,
        loading: false,
      }))
      return { success: true }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Update failed'
      setAuthState(prev => ({ ...prev, loading: false, error: msg }))
      return { error: msg }
    }
  }, [])

  const value: AuthContextValue = {
    ...authState,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateUserMetadata,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
